import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, Download, Trash2, ShieldCheck, Users, Eye, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHead, Card } from "@/pages/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, fmtDateTime, download as downloadFile } from "@/lib/api";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function AccessDialog({ doc, onClose, onSaved }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [sel, setSel] = useState([]);
  const [nda, setNda] = useState(false);
  const [q, setQ] = useState("");
  const [log, setLog] = useState([]);
  useEffect(() => {
    if (!doc) return;
    setSel((doc.allowed || []).map((a) => a.id));
    setNda(!!doc.nda_holders);
    api.get("/companies").then((r) => setCompanies((r.data || []).filter((c) => c.id !== user?.company_id))).catch(() => {});
    api.get(`/dataroom/${doc.id}/log`).then((r) => setLog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [doc, user]);
  const save = async () => {
    try {
      await api.patch(`/dataroom/${doc.id}/access`, { company_ids: sel, nda_holders: nda });
      toast.success("Access updated");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const list = companies.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      {doc && (
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10 max-w-xl max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="access-dialog">
          <DialogTitle className="font-display text-2xl font-normal">Who can access this?</DialogTitle>
          <DialogDescription className="text-slate-400">{doc.category} · {doc.title}. Only your team can see it until you grant access.</DialogDescription>
          <label className="flex items-center gap-3 rounded-xl border border-[#E0218A]/30 p-3 text-sm text-slate-200"><input type="checkbox" checked={nda} onChange={(e) => setNda(e.target.checked)} className="accent-[#E0218A]" data-testid="access-nda-holders" /><Lock className="h-4 w-4 text-[#E0218A]" />Any company that has signed one of your NDAs</label>
          <input className="field" placeholder="Search companies" value={q} onChange={(e) => setQ(e.target.value)} data-testid="access-search-input" />
          <div className="max-h-56 overflow-y-auto divide-y divide-[#2A1240]/10 rounded-xl border border-[#2A1240]/10" data-lenis-prevent>
            {list.map((c) => (
              <label key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-[#2A1240]/5" data-testid={`access-company-${c.id}`}>
                <span className="text-[#2A1240]">{c.name} <span className="text-xs text-slate-400">· {c.company_type}</span></span>
                <input type="checkbox" checked={sel.includes(c.id)} onChange={() => setSel(sel.includes(c.id) ? sel.filter((x) => x !== c.id) : [...sel, c.id])} className="accent-[#E0218A]" />
              </label>
            ))}
          </div>
          <button className="btn-gold" onClick={save} data-testid="access-save-button"><ShieldCheck className="h-4 w-4" />Save access ({sel.length} compan{sel.length === 1 ? "y" : "ies"})</button>
          <div>
            <p className="kicker flex items-center gap-2"><Eye className="h-3.5 w-3.5" />Access log</p>
            {!log.length && <p className="mt-2 text-xs text-slate-400">No external downloads yet.</p>}
            <div className="mt-2 space-y-1 text-xs text-slate-300" data-testid="access-log">{log.map((l) => <p key={l.id}>{l.user_name} ({l.company_name}) · {fmtDateTime(l.at)}</p>)}</div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default function DataRoom() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "mine";
  const [room, setRoom] = useState({ categories: [], items: [] });
  const [shared, setShared] = useState([]);
  const [cat, setCat] = useState("");
  const [upCat, setUpCat] = useState("Media kits");
  const [busy, setBusy] = useState(false);
  const [accessDoc, setAccessDoc] = useState(null);
  const fileRef = useRef(null);
  const load = () => {
    api.get("/dataroom").then((r) => setRoom({ categories: r.data?.categories || [], items: Array.isArray(r.data?.items) ? r.data.items : [] })).catch(() => {});
    api.get("/dataroom/shared").then((r) => setShared(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", upCat);
    try { await api.post("/dataroom", fd); toast.success("Stored privately in your data room"); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const download = async (d) => {
    try { await downloadFile(`/dataroom/${d.id}/download`, d.filename); } catch (e) { toast.error(errMsg(e)); }
  };
  const docs = (tab === "mine" ? room.items : shared).filter((d) => !cat || d.category === cat);
  return (
    <div data-testid="dataroom-page">
      <PageHead kicker="Secure data room" title="Partnership data room">
        {tab === "mine" && <>
          <select className="field !w-auto !py-2.5" value={upCat} onChange={(e) => setUpCat(e.target.value)} data-testid="dataroom-upload-category">{room.categories.map((c) => <option key={c}>{c}</option>)}</select>
          <input type="file" ref={fileRef} className="hidden" onChange={upload} data-testid="dataroom-file-input" />
          <button className="btn-gold !py-2.5" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="dataroom-upload-button"><Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload"}</button>
        </>}
      </PageHead>
      <div className="flex gap-2 mb-5">
        {[["mine", `Our room (${room.items.length})`], ["shared", `Shared with us (${shared.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setSp(k === "mine" ? {} : { tab: k })} className={`rounded-full px-4 py-2 text-sm transition-colors ${tab === k ? "bg-[#2A1240] text-white font-semibold" : "border border-[#2A1240]/15 text-slate-200"}`} data-testid={`dataroom-tab-${k}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4" data-testid="dataroom-categories">
        {["", ...room.categories].map((c) => <button key={c || "all"} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors ${cat === c ? "bg-[#E0218A] text-white font-bold" : "bg-[#2A1240]/5 text-slate-200 hover:bg-[#2A1240]/10"}`}>{c || "All"}</button>)}
      </div>
      <Card className="divide-y divide-[#2A1240]/10" data-testid="dataroom-list">
        {!docs.length && <p className="p-10 text-center text-slate-300" data-testid="dataroom-empty">{tab === "mine" ? "Upload media kits, demographics, financials, proposals, contracts, reports, brand guidelines and legal documents. Everything stays private until you grant access." : "Nothing shared with your company yet."}</p>}
        {docs.map((d) => (
          <div key={d.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`dataroom-doc-${d.id}`}>
            <div className="flex items-start gap-4 min-w-0">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-[#E0218A]/30 text-[#8E2BC4]"><FileText className="h-5 w-5" /></span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#8E2BC4]">{d.category}{d.owner_name ? ` · from ${d.owner_name}` : ""}</p>
                <p className="mt-0.5 text-[#2A1240] truncate">{d.title}</p>
                <p className="text-xs text-slate-400">{kb(d.size || 0)} · {fmtDate(d.created_at)} · {d.uploaded_by}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {tab === "mine" && <span className="inline-flex items-center gap-1.5 text-xs text-slate-300 mr-2" data-testid={`dataroom-access-summary-${d.id}`}>{d.allowed?.length || d.nda_holders ? <><Users className="h-3.5 w-3.5 text-[#E0218A]" />{d.allowed?.length || 0} compan{d.allowed?.length === 1 ? "y" : "ies"}{d.nda_holders ? " + NDA holders" : ""}</> : <><Lock className="h-3.5 w-3.5" />Team only</>} · {d.views || 0} views</span>}
              {tab === "mine" && <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => setAccessDoc(d)} data-testid={`dataroom-access-${d.id}`}><ShieldCheck className="h-3.5 w-3.5" />Access</button>}
              <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => download(d)} data-testid={`dataroom-download-${d.id}`}><Download className="h-3.5 w-3.5" />Download</button>
              {tab === "mine" && <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => window.confirm("Delete this document?") && api.delete(`/dataroom/${d.id}`).then(load)} data-testid={`dataroom-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          </div>
        ))}
      </Card>
      <AccessDialog doc={accessDoc} onClose={() => setAccessDoc(null)} onSaved={load} />
    </div>
  );
}
